import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { clone as cloneSkinnedScene } from 'three/examples/jsm/utils/SkeletonUtils.js'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'

export interface AnimatedModel {
  scene: THREE.Group
  animations: THREE.AnimationClip[]
}

/** Loads Blender-exported GLB character models (served from `public/models/`). Results are cached
 * per URL — clone the returned group before mutating if you need independent instances. */
const loader = new GLTFLoader()
const cache = new Map<string, Promise<THREE.Group>>()
const animatedCache = new Map<string, Promise<AnimatedModel>>()

export function loadModel(url: string): Promise<THREE.Group> {
  let p = cache.get(url)
  if (!p) {
    p = new Promise<THREE.Group>((resolve, reject) => {
      loader.load(url, (gltf) => resolve(gltf.scene), undefined, reject)
    }).catch((err) => {
      // Don't cache a transient failure (server slow / offline / 404) — evict so the next
      // request retries instead of permanently reusing the rejected promise.
      cache.delete(url)
      throw err
    })
    cache.set(url, p)
  }
  return p
}

/** Loads a Blender-authored GLB and preserves its animation clips. The scene is
 * cached as a template; clone it before applying stage-specific transforms. */
export function loadAnimatedModel(url: string): Promise<AnimatedModel> {
  let promise = animatedCache.get(url)
  if (!promise) {
    promise = new Promise<AnimatedModel>((resolve, reject) => {
      loader.load(url, (gltf: GLTF) => resolve({ scene: gltf.scene, animations: gltf.animations }), undefined, reject)
    }).catch((err) => {
      animatedCache.delete(url)
      throw err
    })
    animatedCache.set(url, promise)
  }
  return promise
}

/** Creates a transform-independent scene instance from a cached GLB template.
 * Geometry and materials remain shared and must not be disposed by the instance owner. */
export function cloneModelTemplate(template: THREE.Group): THREE.Group {
  // SkeletonUtils preserves skinned meshes and their bone bindings. Plain
  // Object3D.clone() leaves cloned skins pointing at the cached template bones,
  // which makes imported animation clips appear frozen or deform another actor.
  return cloneSkinnedScene(template) as THREE.Group
}

/** Dispose all geometries + materials under a group (call when removing a loaded model). */
export function disposeModel(obj: THREE.Object3D): void {
  obj.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.geometry) mesh.geometry.dispose()
    const mat = mesh.material
    if (Array.isArray(mat)) mat.forEach((m) => m.dispose())
    else if (mat) mat.dispose()
  })
}
